import {
  Completion,
  CompletionContext,
  CompletionSource,
  insertCompletionText,
} from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';

import { completionPath } from 'dashboard/components-lib/CodeEditor/completionSources/javascript/utils';
import { JsonSchema, JsonSchemaObject } from 'lib/json-schema';

/**
 * Basic completion source for JSON schema.
 */
export const generateJsonSchemaCompletionSource = (
  jsonSchema: JsonSchemaObject,
): CompletionSource => {
  return (context: CompletionContext) => {
    const path = completionPath(context) ?? { path: [], name: '' };
    // Paths will come back with quotes if they are string keys e.g. foo['bar']. returns ['foo', "'bar'"]
    const pathArray = removeQuotesFromPaths(path.path);
    const pathName = path.name;

    // when path array and path name are empty there is nothing to autocomplete
    if (pathArray.length === 0 && pathName === '') return null;

    // Find the matching subschema for the current path
    const subschema = findSubschema(jsonSchema, pathArray);
    if (!subschema || !('properties' in subschema)) {
      return null;
    }

    const completions = getCompletionsFromSchema(subschema);

    return {
      from: context.pos - pathName.length,
      options: completions,
      // the way validFor works is confusing, I recommend referring to code mirror docs https://codemirror.net/examples/autocompletion/
      validFor: /[\w]+$/,
    };
  };
};

const removeQuotesFromPaths = (paths: readonly string[]): string[] => {
  return paths.map((path) => path.replace(/'/g, ''));
};

export function findSubschema(
  schema: JsonSchema,
  pathArray: readonly string[],
): JsonSchema | undefined {
  if (pathArray.length === 0) return schema;

  let target = schema;
  for (const step of pathArray) {
    if (!('properties' in target)) return;

    target = target.properties[step];
    if (!target) return;
  }

  return target;
}

export function getCompletionsFromSchema(schema: JsonSchema): Completion[] {
  if (!('properties' in schema)) return [];

  const completions = Object.entries(schema.properties)
    // map completions to the expected format
    .map(([key, keySchema]) => {
      const isKebabed = key.includes('-');

      return {
        label: key,
        type: 'type' in keySchema ? keySchema.type : '',
        info: keySchema.description || '',
        apply: (view: EditorView, _completion: Completion, from: number, to: number) => {
          // get character before from
          const characterBefore = view.state.doc.sliceString(from - 1, from);

          if (characterBefore === '.' && isKebabed) {
            /**
             *       suggestions
             *       ┌─────────┐
             * foo.  │ bar-baz │ --[enter]-->  foo['bar-baz']
             *       └─────────┘
             *
             */
            view.dispatch(insertCompletionText(view.state, `['${key}']`, from - 1, to));
          } else if (characterBefore === '[') {
            /**
             *      suggestions
             *      ┌─────────┐
             * foo[ │ bar-baz │ --[enter]-->  foo['bar-baz']
             *      └─────────┘
             */
            view.dispatch(insertCompletionText(view.state, `'${key}'`, from, to));
          } else {
            /**
             *      suggestions
             *      ┌─────────┐
             * foo. │ bar     │ --[enter]-->  foo.bar
             *      └─────────┘
             */
            view.dispatch(insertCompletionText(view.state, key, from, to));
          }
        },
      };
    });

  return completions;
}
